import React from 'react';
import videojs from 'video.js';
import vttThumbnails from 'videojs-vtt-thumbnails/src/plugin.js';
import 'videojs-hotkeys';
import classnames from 'classnames';

import SubscriptionModal from '../generalComponents/SubscriptionModal';

import '../../css/player.css';

import FeaturedSignUpCTA from '../../assets/NakedNewsLive/featured-signup.jpg';

import FeaturedClipControls from './FeaturedClipControls';
import PlayButton from './PlayButton';
import VideoLineLoader from './VideoLineLoader';


const RESET_CALL_TO_ACTION_IMAGE_AFTER_TIME_IN_MILLIS = 5000;


const MOUSE_IN_VIDEO_PLAY_TIMEOUT_IN_MILLIS = 700;


class SimpleVideoPlayer extends React.Component {

  state = {
    showSubscriptionModal: false,
    isMuted: true,
    isPlaying: false,
    hovered: false,
    timerId: null,
    ctaTimerId: null,
    isLoading: false,
    posterImage: null,
  };

  playButton = () => {
    if (this.player) {
      if (this.props.isFeaturedClip && this.state.hovered) {
        this.setState({ isPlaying: true, isLoading: false });
        this.player.play().catch((e) => null);

      } else {
        this.setState({ isPlaying: true });
        this.player.play().catch((e) => null);
      }
    }
  };

  stopPlayer = () => {
    const { isFeaturedClip, clip } = this.props;
    this.setState({ isLoading: false });
    if (this.player && isFeaturedClip) {
      this.setState({ hovered: false });
      if (this.state.timerId) {
        clearTimeout(this.state.timerId);
        this.player.src(clip?.clipUrl);
        this.player.pause();
        this.setState({ isPlaying: false });
      }
    }
  };

  onHandleMute = (e) => {
    e.preventDefault();
    this.setState({ isMuted: !this.state.isMuted });
    this.player.muted(!this.state.isMuted);
  };

  decideClipUrls = () => {
    const { clip } = this.props;
    if (clip?.programSegments) {

      return clip?.programSegments?.find((segment) => segment?.freemium).clip?.clipUrls?.SIZE_720;
    }
    if (clip?.clipUrls) {
      return clip?.clipUrls?.SIZE_720 || clip?.clipUrls?.SIZE_480 || clip?.clipUrls?.SIZE_360;
    }

    return clip?.videoUrl || clip?.clipUrl;
  };

  initVideoJs = () => {
    const { clip, user, fanZoneFreeVideo, isFeaturedClip, guestVideoPlayer, isNNLSection } = this.props;
    const srcClipUrl = this.decideClipUrls();
    videojs.registerPlugin('vttThumbnails', vttThumbnails);
    // instantiate Video.js
    this.player = videojs(this.videoNode, this.props, function onPlayerReady() {
      if (!isNNLSection) {
        this.hotkeys({
          volumeStep: 0.1,
          seekStep: 10,
          captureDocumentHotkeys: false,
          documentHotkeysFocusElementFilter: e => e.className.toLowerCase() === 'body',
          alwaysCaptureHotkeys: true,
        });
      }
      this.src(srcClipUrl);
    });

    if (clip?.imageUrl) {
      this.player.poster(clip?.imageUrl);
    }

    if (guestVideoPlayer) {
      const posterImageUrl = clip?.imageUrl || clip?.programSegments?.[0]?.clip?.thumbnailImageUrl;
      this.player.poster(posterImageUrl);
    }

    if (clip?.vtt) {
      this.player.vttThumbnails({
        src: clip?.vtt?.SRC,
        sprite: clip?.vtt?.SPRITE,
      });
      const img1 = document.createElement('img');
      img1.src = clip?.vtt.SPRITE; // Assigning the img src immediately requests the image
    }
    this.player.on('ended', () => {
      this.setState({ isPlaying: false });
      if (!user?.authenticated && !fanZoneFreeVideo && !isFeaturedClip) {
        this.openSubscriptionModal();
      }
      if (!user?.authenticated && !fanZoneFreeVideo && isFeaturedClip) {
        this.player.poster(FeaturedSignUpCTA);
        const timer = setTimeout(() => {
          this.player.poster(clip?.imageUrl);
        }, RESET_CALL_TO_ACTION_IMAGE_AFTER_TIME_IN_MILLIS);
        this.setState({ ctaTimerId: timer });
        this.player.src(clip?.clipUrl);
      }
    });
  };

  componentDidMount() {
    const { clip } = this.props;
    this.initVideoJs();
    const posterImageUrl = clip?.imageUrl || clip?.programSegments?.[0]?.clip?.thumbnailImageUrl;
    this.setState({ posterImage: posterImageUrl });
  }

  componentDidUpdate(prevProps) {
    if ((prevProps.clip?.videoUrl !== this.props.clip?.videoUrl) ||
      (prevProps.clip?.id !== this.props.clip?.id) ||
      (prevProps?.clip?.imageUrl !== this.props.clip?.imageUrl)) {
      this.initVideoJs();
    }
  }

  // destroy player on unmount
  componentWillUnmount() {
    if (this.player) {
      this.player.dispose();
    }
    if (this.state.timerId) {
      clearTimeout(this.state.timerId);
    }
    if (this.state.ctaTimerId) {
      clearTimeout(this.state.ctaTimerId);
    }
  }

  openSubscriptionModal = () => {
    this.setState({ showSubscriptionModal: true });
  };

  closeSubscriptionModal = (e) => {
    if (e) {
      e.preventDefault();
    }
    this.setState({ showSubscriptionModal: false });
  };

  handleMouseEnter = () => {
    if (this.props.isFeaturedClip) {
      this.setState({ hovered: true, isLoading: true });
      const timer = setTimeout(this.playButton, MOUSE_IN_VIDEO_PLAY_TIMEOUT_IN_MILLIS);
      this.setState({ timerId: timer });
    }
  };

  onTogglePlay = (e) => {
    const { isPlaying } = this.state;
    e.preventDefault();
    if (!isPlaying) {
      this.setState({ isPlaying: true });
      this.player.play();

      return;
    }
    if (isPlaying) {
      this.setState({ isPlaying: false });
      this.player.pause();

    }
  };

  render() {
    const { clip, autoPlayVideo, videoClass = false, isFeaturedClip, isNNLSection } = this.props;
    const { showSubscriptionModal, isMuted, isPlaying, isLoading, posterImage } = this.state;
    const videoId = isNNLSection ? '-naked-news-live' : '';

    return (
      <>
        <div
          className="masthead relative"
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.stopPlayer}
        >
          <FeaturedClipControls
            isFeaturedClip={isFeaturedClip}
            isMuted={isMuted}
            isPlaying={isPlaying}
            onMute={this.onHandleMute}
            onTogglePlay={this.onTogglePlay}
          />
          <PlayButton isFeaturedClip={isFeaturedClip} isPlaying={isPlaying} onTogglePlay={this.onTogglePlay} />
          <VideoLineLoader isLoading={isLoading} />
          <div className={classnames("container", { 'guest-video-player': isNNLSection })}>
            <div className="clearfix video-player-container preview" id="video-player-container">
              <div className="full-player">
                <div className="player-zone">
                  <div className="video-player">
                    <div id="vp_18">
                      <div className={`sk-video-player news-player ${videoClass ? 'video-player-trending-page' : ''}`} id="vp_18_wrapper">
                        <video
                          ref={(node) => this.videoNode = node}
                          autoPlay={autoPlayVideo}
                          className="video-js vjs-sk vjs-default-skin vjs-paused vp_18_video-dimensions vjs-fluid vjs-controls-enabled vjs-workinghover vjs-v6 vjs-user-active"
                          controls={!isFeaturedClip}
                          id={`vp_18_video_html5_api${videoId}`}
                          muted={isFeaturedClip}
                          playinline=""
                          poster={posterImage || clip?.imageUrl || clip?.programSegments?.[0]?.clip?.thumbnailImageUrl}
                          preload="none"
                          tabIndex="-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div style={{ fontSize: '14px' }}>
            <SubscriptionModal
              closeModal={this.closeSubscriptionModal}
              show={showSubscriptionModal}
            />
          </div>
        </div>
      </>
    );
  }
}

export default SimpleVideoPlayer;

SimpleVideoPlayer.defaultProps = {
  isNNLSection: false,
  guestVideoPlayer: false,
}
